import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { TarifaService } from '../../services/tarifa/tarifa.service';
import { Tarifa } from '../../domain/tarifa.domain';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-tarifas',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './tarifas.component.html',
  styleUrls: ['./tarifas.component.scss']
})
export class TarifasComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  // Signals para estado reactivo
  tarifas = signal<Tarifa[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: Tarifa[] = [];
  filterValue: string = '';

  // Configuración
  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  // Control de suscripciones y reintentos
  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000;

  // Columnas de la tabla
  columnsTable: Column[] = [
    { field: 'codigo', header: 'Código', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'nombre', header: 'Nombre', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'descripcion', header: 'Descripción', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'vigenciaDesde', header: 'Vigencia Desde', fieldType: 'date', sortable: true, exportable: true, filtrable: false },
    { field: 'vigenciaHasta', header: 'Vigencia Hasta', fieldType: 'date', sortable: true, exportable: true, filtrable: false },
    { field: 'active', header: 'Activa', fieldType: 'boolean-icon', sortable: true, exportable: false, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  // Para filtros y ordenamiento
  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  // Helper para limpiar tabla
  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  constructor(
    private tarifaService: TarifaService,
    private router: Router,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.processLazyEvent(event);
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    this.processLazyEvent(event);
    this.cargar();
  }

  private processLazyEvent(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
  }

  getFilterType(col: Column): string {
    if (!col.fieldType) return 'text';
    if (col.fieldType === 'date') return 'date';
    if (col.fieldType === 'boolean' || col.fieldType === 'boolean-icon') return 'boolean';
    if (col.fieldType === 'number') return 'numeric';
    return 'text';
  }

  getMatchMode(fieldType?: string): string {
    if (fieldType === 'boolean') return 'equals';
    if (fieldType === 'number') return 'equals';
    return 'contains';
  }

  cargar(): void {
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.tarifaService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;

          const tarifasMapeadas = members.map((tarifa: any) => ({
            id: tarifa.id,
            codigo: tarifa.codigo,
            nombre: tarifa.nombre,
            descripcion: tarifa.descripcion,
            vigenciaDesde: tarifa.vigenciaDesde,
            vigenciaHasta: tarifa.vigenciaHasta,
            active: tarifa.active ?? true,
            createdAt: tarifa.createdAt,
            updatedAt: tarifa.updatedAt,
            selected: false
          }));

          this.tarifas.set(tarifasMapeadas);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || tarifasMapeadas.length);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;
        },
        error: (error: any) => {
          this.loading.set(false);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;

          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar tarifas (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);

          const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
            ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
            : `Ocurrió un error al recuperar tarifas (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDetail,
            life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
            sticky: this.errorCount >= this.MAX_ERROR_COUNT
          });

          this.filtersMetadata = {};
          this.multiSortMeta = [];
          this.tarifas.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue = '';
    this.selectedRows = [];
    this.filtersMetadata = {};
    this.multiSortMeta = [];
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleTarifaActiva(tarifa: Tarifa): void {
    if (!tarifa.id) return;

    const nuevoEstado = tarifa.active ?? true;

    const tarifaActualizada: Tarifa = {
      active: nuevoEstado
    };

    this.loading.set(true);
    this.tarifaService.patch(tarifa.id, tarifaActualizada)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: Tarifa) => {
          this.loading.set(false);
          const estadoFinal = resp.active ?? false;
          const accionTxt = estadoFinal ? 'activeda' : 'desactiveda';
          this.messageService.add({
            severity: 'success',
            summary: `Tarifa ${accionTxt}`,
            detail: `La tarifa ha sido ${accionTxt} correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          tarifa.active = !nuevoEstado;
          console.error('Error al actualizar tarifa:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado de la tarifa',
            life: 3000
          });
        }
      });
  }

  editarTarifa(id: number): void {
    this.router.navigate(['/tarifas/edit', id]);
  }

  navigateToCreateTarifa(): void {
    this.router.navigate(['/tarifas/edit/nuevo']);
  }

  eliminarTarifa(tarifa: Tarifa): void {
    this.confirmationService.confirm({
      message: `¿Está seguro que desea desactiver la tarifa ${tarifa.nombre || tarifa.codigo || ''}?`,
      header: 'Confirmar Desactiveción',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactiver',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (!tarifa.id) return;

        this.loading.set(true);
        const tarifaActualizada: Tarifa = {
          active: false
        };

        this.tarifaService.patch(tarifa.id, tarifaActualizada)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Tarifa Desactiveda',
                detail: 'La tarifa ha sido desactiveda correctamente',
                life: 3000
              });
              this.cargar();
            },
            error: (error) => {
              this.loading.set(false);
              console.error('Error al desactiver tarifa:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo desactiver la tarifa',
                life: 3000
              });
            }
          });
      }
    });
  }

  activerTarifa(tarifa: Tarifa): void {
    if (!tarifa.id) return;

    this.loading.set(true);
    const tarifaActualizada: Tarifa = {
      active: true
    };

    this.tarifaService.patch(tarifa.id, tarifaActualizada)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Tarifa Activada',
            detail: 'La tarifa ha sido activeda correctamente',
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al activer tarifa:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo activer la tarifa',
            life: 3000
          });
        }
      });
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-AR');
  }
}
